import React from "react";
import { defaultLocale, langs, locales, routes, showDefaultLocale, type Locale, type Handle, siteHandles } from "../ui";
import { kebabCase } from "change-case";
export type { Locale, Handle } from "../ui";
export { getSite as getCurrentSite } from "../site";

const hostname = import.meta.env.PUBLIC_SITE_URL || globalThis?.location?.origin;
const isUrlRegExp = /^(https?:)?\/\//;

export interface tOptions extends Record<string, any> {
  locale?: Locale;
  ns?: string | Array<string>;
}

export interface TPFunction {
  (path: string | undefined, l?: Locale): string;
}
export interface TRFunction {
  (path: string | undefined, l?: Locale): string;
}

export interface TranslatedObj {
  tp: TPFunction;
  tr: TRFunction;
}

const isServer = typeof window === 'undefined';
export const isString = (obj: unknown) => typeof obj === 'string';

export const isObject = (obj: unknown):obj is Record<string, any> => typeof obj === 'object' && obj !== null;
const getAsArray = (data:React.ReactNode) => (Array.isArray(data) ? data : [data]);

export const nodesToString = (children: React.ReactNode) => {
  if (!children) return '';
  let stringNode = '';

  // do not use `React.Children.toArray`, will fail at object children
  const childrenArray = getAsArray(children);
  // e.g. lorem <br/> ipsum {{ messageCount, format }} dolor <strong>bold</strong> amet
  
  childrenArray.forEach((child, childIndex) => {
    if (isString(child)) {
      stringNode += `${child}`;
    } else if (React.isValidElement<React.PropsWithChildren<Record<string, string | number | boolean | null | undefined>>>(child)) {
      const { props: {children , ...props}, type } = child;
      const propsArr = Object.entries(props).map(([k, v]) => {
        const key = kebabCase(k);
        if(!v && typeof v !== "number")
          return undefined;
        else
          return `${key==="class-name"?"class":key}` + ((v===true) ? "" : `="${v}"`);
      }).filter(p=>typeof p !== "undefined");
      const propsStr = !!propsArr.length ? ` ${propsArr.join(" ")}` : "";
      if(!children)
        stringNode += `<${type}${propsStr}/>`;
      else
        stringNode += `<${type}${propsStr}>${children}</${type}>`;
    } else if (child === null) {
      console.warn(`nodesToString: the passed in value is invalid - seems you passed in a null child.`);
    } else if (isObject(child)) {
      // e.g. lorem {{ value, format }} ipsum
      const { format, ...clone } = child;
      const keys = Object.keys(clone);

      if (keys.length === 1) {
        const value = format ? `${keys[0]}, ${format}` : keys[0];
        stringNode += `{{${value}}}`;
      } else {
        // not a valid interpolation object (can only contain one value plus format)
        console.warn(
          `react-i18next: the passed in object contained more than one variable - the object should look like {{ value, format }} where format is optional.`,
          child,
        );
      }
    } else {
      console.warn(
        `Trans: the passed in value is invalid - seems you passed in a variable like {number} - please pass in variables for interpolation as full objects like {{number}}.`,
        child,
      );
    }
  });

  return stringNode;
};

export function getLocaleWithHandle(handle: string) {
  return Object.entries(siteHandles).find(([, h]) => h === handle)?.[0] as Locale || defaultLocale;
}

export function getCurrentLocale(_locale: Locale, opt?: tOptions) {
  const locale = (_locale in locales) ? _locale as Locale : defaultLocale;
  const pathname = (!showDefaultLocale && locale === defaultLocale) ? '' : `/${locale}`
  const res = Object.assign({
    slug: locale,
    name: locales[locale],
    pathname: pathname,
    hostname: hostname + pathname,
    lang: langs[locale]
  }, useTranslated(locale, opt));
  return res;
}

export const trans = (localizedString: string, children: React.ReactNode, values?: Record<string, string | number | null | undefined>) => {
  
  if(typeof localizedString !== "string") return "";
  if(typeof children !== "object") return "";

  if(!!children)
    localizedString = interpolate(localizedString, nodesToString(children));
  if(!!values)
    Object.entries(values).forEach(([k, v]) => localizedString = localizedString.replace(`{{${k}}}`, `${v}`));
  return localizedString;
}

export const translateGlossaries = (glossaries?: Record<string, string>, key?: string, values?: Record<string, string | number | null | undefined>) => {
  if(typeof glossaries !== "object") return key || glossaries;
  if(typeof key !== "string") return undefined;
  var localizedString = (key in glossaries) ? glossaries[key] : key;
  if(!!values)
    Object.entries(values).forEach(([k, v]) => localizedString = localizedString.replace(`{{${k}}}`, `${v}`));
  return localizedString;
}

export function useTranslatedFunction(lang: Locale) {
  return function translatedFunction(path?: string, l: Locale = lang) {
    if(typeof path !== "string") return undefined;
    // now the url is a source URL, it is unparsed URL
    /*
    //
    var url = isUrlRegExp.test(path) ? new URL(path) : new URL(path, hostname);
    if(url.origin !== hostname) // verify is some origin.
      return url; // if not same origin, return as is.
    */
    if(isUrlRegExp.test(path))
      return undefined;
    var url = new URL(path, hostname);

    // Use `/` split the pathname and remove first index, because the pathname always start with `/`, so the first index is a empty string.
    var slugs = url.pathname.split("/").slice(1);
    // Get the Uri, the uri can not contain the locale path, so if the first index of slugs is the locale, remove it.
    var uri = slugs.slice(slugs[0] in locales ? 1 : 0).join("/");
    // Get the Uri of current locale, if set showDefaultLocale = false, return empty string.
    var localeUri = (!showDefaultLocale && defaultLocale === l) ? "" : l;
    // New a new URL, it is current locale.
    url.pathname = `${localeUri}/${uri}`.replace(/\/$/i, "");
    // return the pathname of parsed URL.
    return url;
  }
}

export function useTranslatedRelative(lang: Locale) {
  return function translatedRelative(path?: string | null, l: Locale = lang) {
    if(typeof path !== "string") return "";
    const translateFunction = useTranslatedFunction(l);
    const url = translateFunction(path);
    if(!url || url.origin !== hostname)
      return path;
    return `${url.pathname}${url.search}${url.hash}`;
  }
}

export function useTranslatedPath(lang: Locale) {
  return function translatedRelative(path?: string, l: Locale = lang) {
    if(typeof path !== "string") return "";
    if(isUrlRegExp.test(path)) return path; 
    const translateFunction = useTranslatedFunction(l);
    const url = translateFunction(path);
    if(!url || url.origin !== hostname)
      return path;
    return `${url.pathname}${url.search}${url.hash}`;
  }
}

export function useTranslated(locale: Locale, ops?: tOptions) {
  return {
    ts: translateGlossaries,
    tp: useTranslatedPath(locale),
    tr: useTranslatedRelative(locale),
  }
}

export const interpolate = (
  localizedString: string,
  referenceString: string,
): string => {
  const tagsRegex = /<([\w\d]+)([^>]*)>/gi;

  const referenceStringMatches = referenceString.match(tagsRegex);

  if (!referenceStringMatches) {
    console.warn(
      "WARNING(astro-i18next): default slot does not include any HTML tag to interpolate! You should use the `t` function directly."
    );
    return localizedString;
  }

  const referenceTags:Array<any> = [];
  referenceStringMatches.forEach((tagNode) => {
    const [, name, attributes] = tagsRegex.exec(tagNode) ?? [];
    referenceTags.push({ name, attributes });

    // reset regex state
    tagsRegex.exec("");
  });

  let interpolatedString = localizedString;
  for (let index = 0; index < referenceTags.length; index++) {
    const referencedTag = referenceTags[index];
    // Replace opening tags
    interpolatedString = interpolatedString.replaceAll(
      `<${index}>`,
      `<${referencedTag.name}${referencedTag.attributes}>`
    );
    // Replace closing tags
    interpolatedString = interpolatedString.replaceAll(
      `</${index}>`,
      `</${referencedTag.name}>`
    );
  }

  return interpolatedString;
};